/**
 * Whether this device agrees to be counted.
 *
 * WHY IT EXISTS. The usage measurement has been running since 12 August 2026
 * on legitimate interest, with no consent collected, which is what article 21
 * of the GDPR attaches a right of objection to. Until this switch, somebody
 * could write and ask us to stop measuring them and we could not do it:
 * `setEnabled` was called nowhere in the repository. We would have published a
 * policy announcing a right the code could not honour.
 *
 * A REFUSAL IS A PRESENCE, NOT A VALUE. Absent means allowed, which keeps
 * today's behaviour for everybody who never opens this screen, and is the
 * default the ticket asked for. Storing the refusal as the presence of a key
 * rather than the string `false` means any unexpected value still reads as a
 * refusal: whichever way the content gets mangled, a refusal is never quietly
 * turned back into a yes. The one case that loses it is `getItem` throwing,
 * which happens when storage is blocked altogether, and there we keep the
 * current behaviour rather than silently stopping the measurement for
 * everybody.
 *
 * CARRIED BY THE ACCOUNT since 15 October 2026 (Asana 1219512555228093,
 * point 2, Maude's decision). Until then it lived here and nowhere else: it
 * survived an update and a sign-out, and was lost on a reinstall and on every
 * other phone. The account now holds it (`/profile/usage-measurement`), and
 * this key stays as the copy the application reads synchronously before every
 * event, before anybody signs in, and when the API cannot be reached.
 *
 * WHICH LOCAL REFUSAL IS WHICH. The value tells two refusals apart:
 *
 * - `account`: this device holds the account's refusal. If the account later
 *   says yes, from another phone, this one follows.
 * - anything else (`true`, written by 2.2.5 and by a refusal the API did not
 *   receive): a refusal the account has not heard of yet. It is carried to the
 *   account at the next sign-in or start, and never quietly dropped, because
 *   the account saying "not refused" only means it was never told.
 *
 * Both still read as a refusal here, so `usageMeasurementIsAllowed` is
 * unchanged and a mangled value still never turns back into a yes.
 */
export const USAGE_MEASUREMENT_REFUSED_KEY = 'usageMeasurementRefused';

type Readable = Pick<Storage, 'getItem'>;
type Writable = Pick<Storage, 'setItem' | 'removeItem'>;

export const usageMeasurementIsAllowed = (storage: Readable = localStorage): boolean => {
  try {
    return storage.getItem(USAGE_MEASUREMENT_REFUSED_KEY) === null;
  } catch {
    return true;
  }
};

/** The value that says this device holds the account's refusal. */
export const HELD_BY_THE_ACCOUNT = 'account';

/** A refusal the account has not heard of yet, as 2.2.5 wrote it. */
const NOT_YET_ON_THE_ACCOUNT = 'true';

export const rememberUsageMeasurement = (
  allowed: boolean,
  storage: Writable = localStorage,
  heldByTheAccount = false,
): void => {
  try {
    if (allowed) {
      storage.removeItem(USAGE_MEASUREMENT_REFUSED_KEY);
      return;
    }
    storage.setItem(USAGE_MEASUREMENT_REFUSED_KEY, heldByTheAccount ? HELD_BY_THE_ACCOUNT : NOT_YET_ON_THE_ACCOUNT);
  } catch {
    // A device that cannot store the choice is a device that keeps measuring,
    // and the screen will show the toggle back on next time it is opened. Not
    // silently pretending it worked is the point.
  }
};

/**
 * What to do once the account has answered, given what this device holds.
 *
 * - `nothing`: the two already agree.
 * - `send-the-local-refusal`: this device refused and the account was never
 *   told, which is every 2.2.5 refusal on its first sign-in with this build.
 * - `note-the-account-holds-it`: both refuse, but this device still thinks
 *   the account was not told.
 * - `refuse-as-the-account-does`: refused on another phone, or before a
 *   reinstall.
 * - `allow-as-the-account-does`: this device held the account's refusal and
 *   the account has since said yes. Only ever from `account`: a refusal the
 *   account never heard of is not overruled by its silence.
 */
export type WhatTheAccountAsks =
  | 'nothing'
  | 'send-the-local-refusal'
  | 'note-the-account-holds-it'
  | 'refuse-as-the-account-does'
  | 'allow-as-the-account-does';

export const whatTheAccountAsks = (accountRefuses: boolean, storage: Readable = localStorage): WhatTheAccountAsks => {
  let local: string | null;
  try {
    local = storage.getItem(USAGE_MEASUREMENT_REFUSED_KEY);
  } catch {
    // Storage is blocked: nothing could be written either, and the device
    // already keeps measuring by the rule above. Nothing to reconcile.
    return 'nothing';
  }

  if (local === null) {
    return accountRefuses ? 'refuse-as-the-account-does' : 'nothing';
  }
  if (local === HELD_BY_THE_ACCOUNT) {
    return accountRefuses ? 'nothing' : 'allow-as-the-account-does';
  }
  return accountRefuses ? 'note-the-account-holds-it' : 'send-the-local-refusal';
};
